import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  Navigate,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { normalizeTimestamp } from "../utils/dateUtils";
import { voiNahda } from "../utils/oikeudet";
import styled from "styled-components";
import {
  Heading,
  Dropdown,
  DropdownItem,
  SearchInput,
  Table as SuomifiTable,
} from "suomifi-ui-components";
import {
  naytaNimi,
  onTunnistautumaton,
  vahvistusStatus,
} from "../utils/kayttajaNimi";
import TilaBadge from "../components/TilaBadge";
import UidCopy from "../components/UidCopy";
import type { Tukipyynto, TukipyyntoTila } from "../types";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import {
  useGetKayttajatQuery,
  useGetOfficersQuery,
  useGetTukipyynnotQuery,
} from "../api/backendApi";
import { officerNaytto } from "../utils/officer";
import { Trans, useTranslation } from "react-i18next";

type SortKey =
  | "tikettinumero"
  | "kayttaja"
  | "uid"
  | "aihe"
  | "kasittelija"
  | "tila"
  | "saapunut"
  | "paivitetty";

type SortDir = "asc" | "desc";

interface TableColumn {
  key: string;
  headerName: string;
  width?: string;
  sortIcon?: "alphabetical" | "generic";
}

interface TableRow {
  id: string;
  tikettinumero: JSX.Element;
  kayttaja: JSX.Element;
  uid: JSX.Element;
  aihe: string;
  kasittelija: JSX.Element;
  tila: JSX.Element;
  saapunut: string;
  paivitetty: string;
  tiketti: Tukipyynto;
}

const TILA_JARJESTYS: Record<TukipyyntoTila, number> = {
  avoin: 0,
  käsittelyssä: 1,
  ratkaistu: 2,
  "self-resolved": 3,
};

const tilaOptions = [
  { labelText: "search.kaikki.tilat", uniqueItemId: "kaikki" },
  { labelText: "search.tila.avoin", uniqueItemId: "avoin" },
  { labelText: "search.tila.kasittelyssa", uniqueItemId: "käsittelyssä" },
  { labelText: "search.tila.ratkaistu", uniqueItemId: "ratkaistu" },
  { labelText: "search.tila.itse", uniqueItemId: "self-resolved" },
];

const aiheOptions = [
  { labelText: "search.kaikki.aiheet", uniqueItemId: "kaikki" },
  { labelText: "search.aihe.nfc", uniqueItemId: "NFC-luku epäonnistui" },
  { labelText: "search.aihe.mrz", uniqueItemId: "MRZ-luku epäonnistui" },
  {
    labelText: "search.aihe.ei_asiakirjaa",
    uniqueItemId: "Ei hyväksyttyä asiakirjaa",
  },
  {
    labelText: "search.aihe.docUpdate",
    uniqueItemId: "Päivitetyn asiakirjan tiedot eivät täsmää",
  },
  {
    labelText: "search.aihe.vertailu",
    uniqueItemId: "Kasvovertailu epäonnistui",
  },
  {
    labelText: "search.aihe.yhdistaminen",
    uniqueItemId: "Tilin yhdistäminen",
  },
];

function formatDate(
  ts: string | { toDate?(): Date } | { seconds?: number } | undefined | null,
  locale: string,
) {
  const isoString = normalizeTimestamp(ts);
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ResultRow = styled.p`
  margin: 0;
  font: ${({ theme }) => theme.typography.bodyText};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const ResultCount = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.blackBase};
  font-variant-numeric: tabular-nums;
`;

const Filters = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  gap: ${({ theme }) => theme.spacing.m};
  flex-wrap: wrap;
  align-items: flex-end;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 18rem;
`;

const TikettiNum = styled.div`
  font-family: "Courier New", monospace;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.brandBase};
  font-weight: 600;
`;

const KayttajaPrimary = styled.div`
  color: ${({ theme }) => theme.colors.blackBase};
  font-size: 0.875rem;
`;

const KayttajaSecondary = styled.div`
  color: ${({ theme }) => theme.colors.depthDark1};
  font-size: 0.75rem;
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const UidWrap = styled.div`
  color: ${({ theme }) => theme.colors.blackBase};
  font-size: 0.8125rem;
`;

const VahvistusAlateksti = styled.div<{ $todennettu: boolean }>`
  display: block;
  color: ${({ theme, $todennettu }) =>
    $todennettu ? theme.colors.successDark1 : theme.colors.depthDark1};
  font-size: 0.6875rem;
  margin-top: ${({ theme }) => theme.spacing.insetXxs};
`;

const EiUid = styled.div`
  color: ${({ theme }) => theme.colors.depthDark1};
  font-size: 0.8125rem;
  font-style: italic;
`;

const KasittelijaText = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const NoKasittelija = styled.span`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-style: italic;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const TableWrapper = styled.div`
  & > div {
    border: none !important;
  }
  /* Hide caption visually, but keep it accessible to screen readers */
  table caption {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Keep SuomifiTable's existing outer borders */
  && table {
    width: 100%;
  }

  /* Header background and text color */
  && table thead,
  && table thead tr,
  && table thead th {
    background: ${({ theme }) => theme.colors.depthLight3} !important;
    color: ${({ theme }) => theme.colors.brandBase} !important;
  }

  /* Header borders: gray top and sides, blue bottom */
  && table thead {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight1} !important;
    border-left: 1px solid ${({ theme }) => theme.colors.depthLight1} !important;
    border-right: 1px solid ${({ theme }) => theme.colors.depthLight1} !important;
    border-bottom: 2px solid ${({ theme }) => theme.colors.brandBase} !important;
  }

  /* Remove individual header cell top borders */
  && table thead tr,
  && table thead th {
    border-top: 0 !important;
  }

  /* Exact typography from the old component */
  && table thead th {
    text-align: left;
    padding: ${({ theme }) => theme.spacing.s}
      ${({ theme }) => theme.spacing.xxs};

    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      "Helvetica Neue", "Noto Sans", Arial, sans-serif, "Apple Color Emoji",
      "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji" !important;

    font-size: 12px !important;
    font-weight: 600 !important;
    font-style: normal;
    line-height: 18px !important;
    letter-spacing: 0.96px !important;
    text-transform: uppercase;
    color: rgb(0, 53, 122) !important;
  }

  /* Sorting buttons and nested header text */
  && table thead th button,
  && table thead th button *,
  && table thead th span {
    background: transparent !important;
    color: rgb(0, 53, 122) !important;

    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      "Helvetica Neue", "Noto Sans", Arial, sans-serif, "Apple Color Emoji",
      "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji" !important;

    font-size: 12px !important;
    font-weight: 600 !important;
    font-style: normal;
    line-height: 18px !important;
    letter-spacing: 0.96px !important;
    text-transform: uppercase;
  }

  /* Sorting button appearance */
  && table thead th button {
    border: 0 !important;
    box-shadow: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
  }

  && table thead th button:hover {
    color: ${({ theme }) => theme.colors.highlightDark1} !important;
  }

  && table thead th button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 2px;
  }

  /* Sorting icons */
  && table thead th button svg {
    width: 0.875rem !important;
    height: 0.875rem !important;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.brandBase} !important;
  }

  && table thead th button svg path {
    stroke: currentColor;
  }

  /* Header spacing */
  && table thead th:first-child {
    padding-left: ${({ theme }) => theme.spacing.s};
  }

  && table thead th:last-child {
    padding-right: ${({ theme }) => theme.spacing.s};
  }

  /* Table rows: preserve SuomifiTable's alternating backgrounds */
  table tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: ${({ theme }) => theme.colors.highlightLight4};
    }

    &:focus-within {
      outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
      outline-offset: -2px;
    }
  }

  /* Table cells */
  table tbody tr td {
    position: relative;
    padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};
  }

  /* Keep UID copy button clickable */
  table tbody tr td button {
    position: relative;
    z-index: 1;
  }

  /* Ticket links */
  table tbody tr td a[data-ticket-link] {
    display: block;
    margin: ${({ theme }) => `-${theme.spacing.s} -${theme.spacing.m}`};
    padding: ${({ theme }) => `${theme.spacing.s} ${theme.spacing.m}`};
    color: inherit;
    text-decoration: none;
    cursor: pointer;
  }

  /* Extend ticket link's clickable area across the cell */
  table tbody tr td a[data-ticket-link]::after {
    content: "";
    position: absolute;
    inset: 0;
  }

  /* UID column does not navigate */
  table tbody tr td:nth-child(3) {
    cursor: default;
  }
`;

const getTicketHref = (numero: string) =>
  `/tukipyynnot/${numero.replace("#", "")}`;

const VALIDIT_SORT_KEYT: SortKey[] = [
  "tikettinumero",
  "kayttaja",
  "uid",
  "aihe",
  "kasittelija",
  "tila",
  "saapunut",
  "paivitetty",
];

const columns: TableColumn[] = [
  {
    key: "tikettinumero",
    headerName: "search.headers.tiketti",
    sortIcon: "generic",
  },
  {
    key: "kayttaja",
    headerName: "search.headers.kayttaja",
  },
  {
    key: "uid",
    headerName: "search.headers.uid",
    width: "11rem",
  },
  {
    key: "aihe",
    headerName: "search.headers.aihe",
  },
  {
    key: "kasittelija",
    headerName: "search.headers.kasittelija",
    width: "9rem",
  },
  {
    key: "tila",
    headerName: "search.headers.tila",
    width: "7rem",
  },
  {
    key: "saapunut",
    headerName: "search.headers.saapunut",
    width: "8.5rem",
    sortIcon: "generic",
  },
  {
    key: "paivitetty",
    headerName: "search.headers.tapahtuma",
    width: "8.5rem",
    sortIcon: "generic",
  },
];

export default function TukipyyntoLista() {
  const { t, i18n } = useTranslation(["tukipyyntoLista", "common"]);
  const dateLocale = i18n.language === "en" ? "en-GB" : "fi-FI";
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    todellinenRooli,
    isLoading,
    kayttaja: virkailija,
  } = useTodellinenRooli();
  const submittedHaku = searchParams.get("haku") ?? "";
  const [hakuteksti, setHakuteksti] = useState<string>(submittedHaku);
  const taulukkoRef = useRef<HTMLTableElement>(null);

  const { data: tukipyynnot = [] } = useGetTukipyynnotQuery();
  const { data: officers = [] } = useGetOfficersQuery();

  const ticketUserIds = Array.from(
    new Set(tukipyynnot.map((t) => t.kayttajaUid)),
  );

  const { data: kayttajat = [], isLoading: kayttajatLoading } =
    useGetKayttajatQuery(
      ticketUserIds.length > 0 ? { userIds: ticketUserIds } : undefined,
      { skip: ticketUserIds.length === 0 },
    );

  const palautusTila = location.state as {
    palauttavaRivi?: string;
    listScrollY?: number;
  } | null;
  const palauttavaRivi = palautusTila?.palauttavaRivi ?? null;
  const listScrollY = palautusTila?.listScrollY;

  useEffect(() => {
    document.body.style.overflow = "auto";
    document.body.style.position = "static";
    document.body.removeAttribute("data-fi-modal");
  }, []);

  useLayoutEffect(() => {
    if (!palauttavaRivi) return;
    if (typeof listScrollY === "number") {
      window.scrollTo(0, listScrollY);
    }
    const row = taulukkoRef.current?.querySelector(
      `[data-tukipyynto-id="${palauttavaRivi}"]`,
    );
    if (row instanceof HTMLElement) {
      const r = row.getBoundingClientRect();
      const naekyvyydessa = r.top >= 0 && r.bottom <= window.innerHeight;
      if (!naekyvyydessa) {
        row.scrollIntoView({ block: "nearest", behavior: "auto" });
      }
    }
  }, [palauttavaRivi, listScrollY]);

  const loggedInOfficerId = virkailija?.id;

  const kasittelijaOptions = [
    { labelText: "search.kaikki.kasittelijat", uniqueItemId: "kaikki" },
    { labelText: "search.ei.kasittelijaa", uniqueItemId: "ei-kasittelijaa" },
    ...(loggedInOfficerId && officers.find((o) => o.id === loggedInOfficerId)
      ? [
          {
            labelText: officerNaytto({
              id: loggedInOfficerId,
              name: {
                givenName: virkailija.name.givenName ?? "",
                familyName: virkailija?.name.familyName ?? "",
              },
            } as any).nimi,
            uniqueItemId: loggedInOfficerId,
          },
        ]
      : []),
    ...officers
      .filter((o) => o.id !== loggedInOfficerId)
      .map(officerNaytto)
      .map((v) => ({
        labelText: v.nimi,
        uniqueItemId: v.uid,
      })),
  ];

  const tilaSuodatin = searchParams.get("tila") ?? "kaikki";
  const kasittelijaSuodatin = searchParams.get("kasittelija") ?? "kaikki";
  const aiheSuodatin = searchParams.get("aihe") ?? "kaikki";

  const sortKeyParam = searchParams.get("sort");
  const sortKey: SortKey | null =
    sortKeyParam && VALIDIT_SORT_KEYT.includes(sortKeyParam as SortKey)
      ? (sortKeyParam as SortKey)
      : null;
  const sortDir: SortDir = searchParams.get("dir") === "desc" ? "desc" : "asc";

  const [sortState, setSortState] = useState<{
    columnKey: string;
    direction: "asc" | "desc";
  } | null>(sortKey ? { columnKey: sortKey, direction: sortDir } : null);

  function paivitaParams(updates: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "" || value === "kaikki") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    setSearchParams(next, { replace: true });
  }

  function setTilaSuodatin(v: string) {
    paivitaParams({ tila: v });
  }

  function setKasittelijaSuodatin(v: string) {
    paivitaParams({ kasittelija: v });
  }

  function setAiheSuodatin(v: string) {
    paivitaParams({ aihe: v });
  }

  function handleTableSort(columnKey: string, direction: "asc" | "desc") {
    setSortState({ columnKey, direction });
    paivitaParams({ sort: columnKey, dir: direction });
  }

  function getSortValue(t: Tukipyynto, key: SortKey): string | number {
    switch (key) {
      case "tikettinumero":
        return parseInt(t.tikettinumero.replace(/\D/g, ""), 10) || 0;
      case "kayttaja": {
        const k = kayttajat.find((x) => x.uid === t.kayttajaUid);
        if (k && !onTunnistautumaton(k)) {
          const nimi = `${k.sukunimi ?? ""} ${k.etunimi ?? ""}`.trim();
          return nimi
            ? nimi.toLowerCase()
            : (k.sahkoposti ?? t.kayttajaNimi).toLowerCase();
        }
        return ((k?.sahkoposti ?? t.kayttajaNimi) || "").toLowerCase();
      }
      case "uid":
        const k = kayttajat.find((x) => x.uid === t.kayttajaUid);
        return k?.customUid ? k.customUid.toLowerCase() : "";
      case "aihe":
        return t.aihe ? t.aihe.toLowerCase() : "";
      case "kasittelija":
        return ((t.kasittelijaNimi ?? "") || "zzz").toLowerCase();
      case "tila":
        return TILA_JARJESTYS[t.tila];
      case "saapunut":
        return new Date(normalizeTimestamp(t.saapunut)).getTime();
      case "paivitetty":
        return new Date(normalizeTimestamp(t.paivitetty)).getTime();
    }
  }

  const hakuNormalisoitu = submittedHaku
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

  function handleHakuChange(value: string | number) {
    const v = String(value ?? "");
    setHakuteksti(v);
    if (v.trim() === "") {
      paivitaParams({ haku: null });
    }
  }

  function handleHakuSearch(value: string) {
    paivitaParams({ haku: value });
  }

  const suodatetut = tukipyynnot
    .filter((t) => {
      if (tilaSuodatin !== "kaikki" && t.tila !== tilaSuodatin) return false;
      if (
        kasittelijaSuodatin === "ei-kasittelijaa" &&
        t.kasittelijaUid !== null
      )
        return false;
      if (
        kasittelijaSuodatin !== "kaikki" &&
        kasittelijaSuodatin !== "ei-kasittelijaa" &&
        t.kasittelijaUid !== kasittelijaSuodatin
      )
        return false;
      if (aiheSuodatin !== "kaikki" && t.aihe !== aiheSuodatin) return false;

      if (hakuNormalisoitu) {
        const k = kayttajat.find((x) => x.uid === t.kayttajaUid);
        const fields = [
          t.tikettinumero,
          t.aihe,
          t.kasittelijaNimi ?? "",
          k?.customUid ?? "",
          t.kayttajaNimi,
          k?.sahkoposti ?? "",
          k?.etunimi ?? "",
          k?.sukunimi ?? "",
          formatDate(t.saapunut, dateLocale),
          formatDate(t.paivitetty, dateLocale),
        ];
        const osuu = fields.some(
          (f) => f && f.toLowerCase().includes(hakuNormalisoitu),
        );
        if (!osuu) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (sortKey) {
        const av = getSortValue(a, sortKey);
        const bv = getSortValue(b, sortKey);
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      }
      if (tilaSuodatin === "avoin" || tilaSuodatin === "kaikki") {
        const aTime = new Date(normalizeTimestamp(a.saapunut)).getTime();
        const bTime = new Date(normalizeTimestamp(b.saapunut)).getTime();
        return aTime - bTime;
      }
      const aTime = new Date(normalizeTimestamp(a.paivitetty)).getTime();
      const bTime = new Date(normalizeTimestamp(b.paivitetty)).getTime();
      return bTime - aTime;
    });

  const onSuodatettu =
    tilaSuodatin !== "kaikki" ||
    kasittelijaSuodatin !== "kaikki" ||
    aiheSuodatin !== "kaikki" ||
    hakuNormalisoitu !== "";

  const handleRowClick = useCallback(
    (numero: string) => {
      navigate(getTicketHref(numero), {
        state: { listSearch: location.search, listScrollY: window.scrollY },
      });
    },
    [navigate, location.search],
  );

  // ✅ Memoize rows with stable click handler
  const rows: TableRow[] = useMemo(
    () =>
      suodatetut.map((tp) => {
        const tpKayttaja = kayttajat.find((k) => k.uid === tp.kayttajaUid);
        const tunnistautumaton = tpKayttaja
          ? onTunnistautumaton(tpKayttaja)
          : false;

        const isUserLoading =
          kayttajatLoading &&
          !tpKayttaja &&
          ticketUserIds.includes(tp.kayttajaUid);

        const sahkopostiOsoite = tpKayttaja?.sahkoposti ?? tp.kayttajaNimi;
        const nimiTekstinä =
          tpKayttaja && !tunnistautumaton ? naytaNimi(tpKayttaja) : null;
        const primaryTeksti = isUserLoading
          ? t("common:ladataan")
          : nimiTekstinä ?? sahkopostiOsoite;
        const secondaryTeksti = nimiTekstinä ? sahkopostiOsoite : null;

        return {
          id: tp.tikettinumero.replace("#", ""),
          tikettinumero: <TikettiNum>{tp.tikettinumero}</TikettiNum>,
          kayttaja: (
            <>
              <KayttajaPrimary>{primaryTeksti}</KayttajaPrimary>
              {secondaryTeksti && (
                <KayttajaSecondary>{secondaryTeksti}</KayttajaSecondary>
              )}
            </>
          ),
          uid: tunnistautumaton ? (
            <EiUid>—</EiUid>
          ) : (
            <>
              <UidWrap>
                <UidCopy
                  uid={tpKayttaja?.customUid ?? ""}
                  todennettu={tpKayttaja?.henkilollisyysTodennettu ?? false}
                />
              </UidWrap>
              <div>
                {tpKayttaja && !tunnistautumaton && (
                  <VahvistusAlateksti
                    $todennettu={tpKayttaja.henkilollisyysTodennettu}
                  >
                    {t(vahvistusStatus(tpKayttaja), { ns: "common" })}
                  </VahvistusAlateksti>
                )}
              </div>
            </>
          ),
          aihe: t(`common:tukipyynto.aihe.${tp.aihe}`),
          kasittelija: tp.kasittelijaNimi ? (
            <KasittelijaText>{tp.kasittelijaNimi}</KasittelijaText>
          ) : (
            <NoKasittelija>{t("search.ei.kasittelijaa")}</NoKasittelija>
          ),
          tila: <TilaBadge tila={tp.tila as TukipyyntoTila} />,
          saapunut: formatDate(tp.saapunut, dateLocale),
          paivitetty: formatDate(tp.paivitetty, dateLocale),
          tiketti: tp,
        };
      }),
    [suodatetut, kayttajat, kayttajatLoading, ticketUserIds, t],
  );

  function tableSortedAriaLiveText(
    columnLabel: string,
    direction: "asc" | "desc",
  ): string {
    // ✅ Use translation with variables
    return t("aria.lajiteltu", {
      sarake: columnLabel,
      suunta: direction === "asc" ? t("aria.nouseva") : t("aria.laskeva"),
    });
  }

  useEffect(() => {
    const table = taulukkoRef.current;
    if (!table) return;

    const buttons =
      table.querySelectorAll<HTMLButtonElement>("thead th button");

    buttons.forEach((button, index) => {
      const column = columns[index];
      if (!column) return;

      const columnLabel = t(column.headerName);

      const sortingDescription =
        sortKey === column.key
          ? t("aria.nykyinenJarjestys", {
              suunta: sortDir === "asc" ? t("aria.nouseva") : t("aria.laskeva"),
            })
          : "";

      button.setAttribute(
        "aria-label",
        t("aria.jarjestaLabel", {
          sarake: columnLabel,
          jarjestys: sortingDescription,
        }),
      );
    });
  }, [t, i18n.language, sortKey, sortDir]);

  if (isLoading) {
    return <>Ladataan...</>;
  }

  if (!voiNahda("navi.tukipyynnot", todellinenRooli?.profiili)) {
    return <Navigate to="/" replace />;
  }

  return (
    <Page>
      <TopRow>
        <Heading variant="h1">
          {t(`sivut.tukipyynnot`, { ns: "common" })}{" "}
        </Heading>
      </TopRow>

      <Filters>
        <SearchWrap>
          <SearchInput
            labelText={t("search.label")}
            searchButtonLabel={t("search.label")}
            clearButtonLabel={t("search.clear")}
            visualPlaceholder={t("search.placeHolder")}
            value={hakuteksti}
            onChange={handleHakuChange}
            onSearch={handleHakuSearch}
            fullWidth
          />
        </SearchWrap>
        <Dropdown
          labelText={t("search.tila.label")}
          value={tilaSuodatin}
          onChange={(value) => setTilaSuodatin(value)}
        >
          {tilaOptions.map((o) => (
            <DropdownItem key={o.uniqueItemId} value={o.uniqueItemId}>
              {t(o.labelText)}
            </DropdownItem>
          ))}
        </Dropdown>
        <Dropdown
          labelText={t("search.kasittelija")}
          value={kasittelijaSuodatin}
          onChange={(value) => setKasittelijaSuodatin(value)}
        >
          {kasittelijaOptions.map((o) => (
            <DropdownItem key={o.uniqueItemId} value={o.uniqueItemId}>
              {t(o.labelText)}
            </DropdownItem>
          ))}
        </Dropdown>
        <Dropdown
          labelText={t("search.aihe.label")}
          value={aiheSuodatin}
          onChange={(value) => setAiheSuodatin(value)}
        >
          {aiheOptions.map((o) => (
            <DropdownItem key={o.uniqueItemId} value={o.uniqueItemId}>
              {t(o.labelText)}
            </DropdownItem>
          ))}
        </Dropdown>
      </Filters>

      <ResultRow role="status" aria-live="polite">
        <Trans
          ns="tukipyyntoLista"
          i18nKey={onSuodatettu ? "tulokset.suodatettu" : "tulokset.kaikki"}
          count={onSuodatettu ? tukipyynnot.length : suodatetut.length}
          values={{ count: suodatetut.length, total: tukipyynnot.length }}
          components={{ b: <ResultCount /> }}
        />
      </ResultRow>
      <TableWrapper
        onClick={(event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;

          const rowElement = target.closest("tbody tr");
          if (!rowElement) return;

          // UID-sarakkeen klikkaus ei avaa tukipyyntöä.
          const cell = target.closest("td");
          if (cell?.cellIndex === 2) return;

          // Erilliset painikkeet ja jo navigoinnin hoitavat solut käsitellään omissa
          // tapahtumankäsittelijöissään.
          if (target.closest("button, a, [role=button]")) return;

          const ticketLink = rowElement.querySelector<HTMLAnchorElement>(
            "a[data-ticket-link]",
          );
          const ticketNumber = ticketLink?.dataset.ticketNumber;
          if (ticketNumber) handleRowClick(ticketNumber);
        }}
      >
        <SuomifiTable
          ref={taulukkoRef}
          columns={columns.map((col) => ({
            key: col.key,
            labelText: t(col.headerName),
            headerName: t(col.headerName),
            width: col.width,
            sortable: true,
            sortIcon: col.sortIcon as "alphabetical" | "generic" | undefined, // Explicitly cast to the correct type
          }))}
          data={
            rows.map((row) => ({
              ...row,
              tikettinumero: (
                <a
                  data-ticket-link
                  data-ticket-number={row.tiketti.tikettinumero}
                  href={getTicketHref(row.tiketti.tikettinumero)}
                  onClick={(event) => {
                    // Preserve native browser behavior for middle-click and modifier-click.
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    handleRowClick(row.tiketti.tikettinumero);
                  }}
                  aria-label={t("avaaAria", {
                    tiketti: row.tiketti.tikettinumero,
                    aihe: t(`common:tukipyynto.aihe.${row.tiketti.aihe}`),
                  })}
                >
                  <TikettiNum>{row.tiketti.tikettinumero}</TikettiNum>
                </a>
              ),
              kayttaja: (
                <a
                  data-ticket-link
                  data-ticket-number={row.tiketti.tikettinumero}
                  href={getTicketHref(row.tiketti.tikettinumero)}
                  onClick={(event) => {
                    // Preserve native browser behavior for middle-click and modifier-click.
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    handleRowClick(row.tiketti.tikettinumero);
                  }}
                >
                  <div>{row.kayttaja}</div>
                </a>
              ),
              uid: (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    cursor: "default",
                    padding: "0.5rem 0",
                    minHeight: "2.5rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "0.25rem",
                  }}
                >
                  {row.uid}
                </div>
              ),
              aihe: (
                <a
                  data-ticket-link
                  data-ticket-number={row.tiketti.tikettinumero}
                  href={getTicketHref(row.tiketti.tikettinumero)}
                  onClick={(event) => {
                    // Preserve native browser behavior for middle-click and modifier-click.
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    handleRowClick(row.tiketti.tikettinumero);
                  }}
                >
                  {row.aihe}
                </a>
              ),
              kasittelija: (
                <a
                  data-ticket-link
                  data-ticket-number={row.tiketti.tikettinumero}
                  href={getTicketHref(row.tiketti.tikettinumero)}
                  onClick={(event) => {
                    // Preserve native browser behavior for middle-click and modifier-click.
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    handleRowClick(row.tiketti.tikettinumero);
                  }}
                >
                  {row.kasittelija}
                </a>
              ),
              tila: (
                <a
                  data-ticket-link
                  data-ticket-number={row.tiketti.tikettinumero}
                  href={getTicketHref(row.tiketti.tikettinumero)}
                  onClick={(event) => {
                    // Preserve native browser behavior for middle-click and modifier-click.
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    handleRowClick(row.tiketti.tikettinumero);
                  }}
                >
                  {row.tila}
                </a>
              ),
              saapunut: (
                <a
                  data-ticket-link
                  data-ticket-number={row.tiketti.tikettinumero}
                  href={getTicketHref(row.tiketti.tikettinumero)}
                  onClick={(event) => {
                    // Preserve native browser behavior for middle-click and modifier-click.
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    handleRowClick(row.tiketti.tikettinumero);
                  }}
                >
                  {row.saapunut}
                </a>
              ),
              paivitetty: (
                <a
                  data-ticket-link
                  data-ticket-number={row.tiketti.tikettinumero}
                  href={getTicketHref(row.tiketti.tikettinumero)}
                  onClick={(event) => {
                    // Preserve native browser behavior for middle-click and modifier-click.
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    handleRowClick(row.tiketti.tikettinumero);
                  }}
                >
                  {row.paivitetty}
                </a>
              ),
            })) as any
          }
          caption={t(`sivut.tukipyynnot`, { ns: "common" })}
          defaultSort={
            sortState
              ? {
                  columnKey: sortState.columnKey,
                  direction: sortState.direction,
                }
              : undefined
          }
          tableSortCallback={(columnKey, direction) =>
            handleTableSort(columnKey, direction)
          }
          tableSortedAriaLiveText={(columnLabel, direction) =>
            tableSortedAriaLiveText(columnLabel, direction)
          }
        />
      </TableWrapper>
    </Page>
  );
}
